import Icon from '@/components/common/Icon';
import { Spinner } from '@/components/ui';
import { cn } from '@/utils';

export type CardState = 'disconnected' | 'loading' | 'authenticated' | 'error';

interface Props {
  title: string;
  description: string;
  emoji?: string;
  icon?: string;
  connectToWallet?: boolean;
  onClick?: Function;
  loading?: boolean;
  state?: CardState;
  errorMessage?: string | null;
  walletAddress?: string;
  onDisconnect?: () => void;
}

export default function Card({
  title,
  description,
  emoji,
  icon,
  onClick,
  connectToWallet,
  loading,
  state,
  errorMessage,
  walletAddress,
  onDisconnect,
}: Props) {
  const effectiveState: CardState =
    state ||
    (loading ? 'loading' : 'disconnected');

  return (
    <div
      className={cn(
        'featured-card h-full relative overflow-hidden bg-black-300 rounded-20 p-[1px] group/cardContent',
        'after:w-full after:h-full after:absolute after:top-0 after:left-0 after:bg-[radial-gradient(600px_circle_at_var(--mouse-x)_var(--mouse-y),#ffd700,transparent_30%)] after:z-[1] after:transition-opacity after:duration-500 after:opacity-0 group-hover/cards:after:opacity-100',
        connectToWallet && 'cursor-pointer',
        effectiveState === 'loading' && 'pointer-events-none select-none cursor-wait',
        effectiveState === 'authenticated' && 'ring-1 ring-green-400/40 hover:ring-green-400/80',
        effectiveState === 'error' && 'ring-1 ring-error-600/40'
      )}
      onClick={() => {
        if (effectiveState === 'loading') return;
        onClick?.();
      }}
    >
      <div
        className="relative z-[2]
        rounded-20 h-full
        bg-black-300
      after:w-full after:h-full after:absolute after:top-0 after:left-0 
      after:bg-[radial-gradient(600px_circle_at_var(--mouse-x)_var(--mouse-y),#ffd7001a,transparent_30%)] after:z-[1] after:transition-opacity after:duration-500 after:opacity-0 
      group-hover/cardContent:after:opacity-100
      "
      >
        <div className="relative h-full z-[2] bg-black-300/60 backdrop-blur-md flex flex-col justify-start rounded-20 p-6">
          <div className="w-full flex justify-between items-start mb-2">
            {emoji && (
              <span className="bg-white/5 rounded-12 p-2 w-10 h-10 shrink-0">
                {emoji}
              </span>
            )}
            {icon && (
              <img
                src={icon}
                alt={title}
                className="w-10 h-10 bg-white/5 rounded-12 p-2.5 shrink-0"
              />
            )}
            <div className="ms-3 flex-1">
              <h2 className="text-16 text-white text-start me-auto mb-2 font-medium">
                {title}
              </h2>
              <p className="text-14 text-gray-300 mb-2 text-start leading-relaxed">
                {description}
              </p>
            </div>
            <div className="ms-2 shrink-0">
              {connectToWallet && (
                <>
                  {effectiveState === 'loading' && (
                    <Spinner className="size-6" iconClassName="size-6 text-primary-500" />
                  )}
                  {effectiveState === 'authenticated' && (
                    <div className="flex items-center gap-1.5 bg-green-400/10 border border-green-400/30 rounded-full px-2.5 py-1 text-green-400 text-12 font-medium">
                      <span className="size-2 rounded-full bg-green-400 animate-pulse" />
                      <span>Ready</span>
                      <Icon name="ArrowRight" size={16} className="text-green-400" />
                    </div>
                  )}
                  {effectiveState === 'error' && (
                    <div className="flex items-center gap-1 bg-error-600/10 border border-error-600/30 rounded-full px-2.5 py-1 text-error-500 text-12 font-medium">
                      <Icon name="Danger" size={14} className="text-error-500" />
                      <span>Error</span>
                    </div>
                  )}
                  {effectiveState === 'disconnected' && (
                    <Icon
                      name="ArrowRight"
                      size={24}
                      color="#FFD700"
                      className="transition-transform group-hover/cardContent:translate-x-1"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {connectToWallet && effectiveState === 'loading' && (
            <div className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-12 bg-white/5 border border-white/10 text-grayScale-300 text-12">
              <Spinner className="size-4 shrink-0" iconClassName="size-4 text-primary-500" />
              <span className="text-start">Checking authorization and account status...</span>
            </div>
          )}

          {connectToWallet && effectiveState === 'authenticated' && (
            <div className="mt-3 flex items-center justify-between px-3.5 py-2.5 rounded-12 bg-green-400/10 border border-green-400/30 text-13 transition-colors hover:bg-green-400/15">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-green-400" />
                <span className="text-green-400 font-medium">Logged in</span>
                {walletAddress && (
                  <span className="text-gray-400 text-12 font-mono">
                    ({walletAddress.slice(0, 6)}...{walletAddress.slice(-4)})
                  </span>
                )}
              </div>
              <span className="text-primary-500 font-medium text-12 flex items-center gap-1">
                Click to enter &rarr;
              </span>
            </div>
          )}

          {connectToWallet && effectiveState === 'error' && (
            <div className="mt-3 p-3 rounded-12 bg-error-600/10 border border-error-600/30 text-start">
              <div className="flex items-start gap-2">
                <Icon name="Danger" size={18} className="text-error-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-13 text-error-500 font-medium leading-snug">
                    {errorMessage ||
                      'To access this panel, please make your initial investment on the 0xArbitrage platform.'}
                  </p>
                </div>
              </div>
              {onDisconnect && (
                <div className="mt-2.5 pt-2 border-t border-error-600/20 flex justify-end">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDisconnect();
                    }}
                    className="text-12 text-gray-300 hover:text-white underline cursor-pointer transition-colors"
                  >
                    Switch or Disconnect Wallet
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
